import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { FileText, AlertCircle, Download, ExternalLink, Import } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/api';

const BankStatementViewer = ({ statement, loading = false, reportId, onImport, canImport = false }) => {
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [imported, setImported] = useState(false);

  const handleViewStatement = (page) => {
    if (statement && statement.file_url) {
      const pageHash = page ? `#page=${page}` : '';
      window.open(`${statement.file_url}${pageHash}`, '_blank');
    }
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      setError(null);
      const result = await apiService.importBankStatement(reportId);
      if (onImport) {
        onImport(result.items);
      }
      setImported(true);
    } catch (err) {
      setError(err.message || 'Failed to import bank statement');
    } finally {
      setImporting(false);
    }
  };

  if (loading || importing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!statement) {
    return (
      <Alert 
        severity="warning" 
        icon={<AlertCircle />}
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">
            No bank statement is available for this period.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact your service manager to provide the bank statement.
          </Typography>
        </Box>
      </Alert>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FileText size={24} />
          <Typography variant="h6">Bank Statement</Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Typography variant="body2">
            <strong>Uploaded by:</strong> {statement.uploaded_by_name}
          </Typography>
          <Typography variant="body2">
            <strong>Uploaded on:</strong> {format(new Date(statement.uploaded_at), 'MM/dd/yyyy h:mm a')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please review your bank statement and verify that all expenses match the items you've entered.
          For any charges on the statement without corresponding receipts, add them to the Missing Receipt Ledger.
        </Typography>

        {statement.page_count > 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This statement is {statement.page_count} pages. The line that says to continue to page 2 is
            instructions on the form. Every page is included in the PDF sent to billing.
          </Alert>
        )}

        {statement.file_url && (
          <Box
            sx={{
              mb: 2,
              height: 720,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            <iframe
              title="Bank statement"
              src={statement.file_url}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ExternalLink size={18} />}
            onClick={() => handleViewStatement()}
          >
            View Statement
          </Button>
          {statement.page_count > 1 && (
            <Button
              variant="outlined"
              startIcon={<ExternalLink size={18} />}
              onClick={() => handleViewStatement(2)}
            >
              Open page 2
            </Button>
          )}
          {canImport && !imported && (
            <Button
              variant="contained"
              startIcon={<Import size={18} />}
              onClick={handleImport}
              color="primary"
            >
              Import Expenses
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BankStatementViewer;